/**
 * CancelamentoDialog - Componente extraído do AgendamentosPage
 * Gerencia cancelamentos simples e desligamentos definitivos
 * 
 * FASE 1 da refatoração - Extração de diálogos isolados
 */
import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Loader2, XCircle, FileText, AlertCircle, Users } from 'lucide-react';
import { formatDateTime } from '../../utils/dateUtils';

// Estado inicial do dialog
export const initialCancelamentoState = {
  open: false,
  agendamento: null,
  justificativa: '',
  loading: false,
  step: 'escolha', // 'escolha' | 'simples' | 'definitivo'
  tipoCancelamento: null, // 'simples' | 'definitivo'
  motivoDesligamento: '',
  encaminhamento: '',
  // Escopo da exclusão (apenas Recepção/Admin escolhem). 'profissional' = só este profissional | 'clinica' = todos
  escopoDesligamento: 'profissional',
  // ID do profissional do agendamento atual (preenchido ao abrir o dialog)
  escopoProfissionalId: null,
  // Nome do profissional do agendamento atual (apenas para exibição no rádio)
  escopoProfissionalNome: ''
};

/**
 * CancelamentoDialog Component
 * 
 * @param {Object} props
 * @param {Object} props.dialogState - Estado do dialog (cancelamentoDialog)
 * @param {Function} props.setDialogState - Setter do estado (setCancelamentoDialog)
 * @param {Object} props.user - Usuário logado
 * @param {Function} props.onConfirm - Callback quando confirma cancelamento
 */
const CancelamentoDialog = ({ 
  dialogState, 
  setDialogState, 
  user,
  authFetch,
  onConfirm 
}) => {
  // Preview do impacto do desligamento (qtd agendamentos, profissionais afetados)
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  // Nome digitado pelo usuário para confirmação (precisa bater com nome_completo do paciente)
  const [nomeConfirmacao, setNomeConfirmacao] = useState('');

  const pacienteId = dialogState.agendamento?.paciente_id;
  const pacienteNome = dialogState.agendamento?.pacientes?.nome_completo || '';

  // Recepção/Admin podem escolher o escopo. Profissional sempre fica restrito.
  const podeEscolherEscopo = user?.perfil === 'recepcao' || user?.perfil === 'administrador';
  const escopoRestritoProfissional = !podeEscolherEscopo || dialogState.escopoDesligamento === 'profissional';

  // Quando entra em 'definitivo', busca preview e zera input de confirmação
  useEffect(() => {
    if (dialogState.step !== 'definitivo') {
      setNomeConfirmacao('');
      setPreview(null);
      return;
    }
    if (!pacienteId || !authFetch) return;

    let cancelled = false;
    setLoadingPreview(true);
    setPreview(null);
    setNomeConfirmacao('');

    (async () => {
      try {
        // Quando o escopo for restrito a UM profissional, envia ?profissional_id=...
        const profParam = (escopoRestritoProfissional && dialogState.escopoProfissionalId && podeEscolherEscopo)
          ? `?profissional_id=${dialogState.escopoProfissionalId}`
          : '';
        const res = await authFetch(`/agendamentos/preview-desligamento/${pacienteId}${profParam}`);
        if (!cancelled) setPreview(res?.data || res);
      } catch (err) {
        // Falha silenciosa — não bloqueia o fluxo, apenas oculta preview
        console.error('Erro ao buscar preview de desligamento:', err);
      } finally {
        if (!cancelled) setLoadingPreview(false);
      }
    })();

    return () => { cancelled = true; };
  }, [dialogState.step, pacienteId, authFetch, escopoRestritoProfissional, dialogState.escopoProfissionalId, podeEscolherEscopo]);

  // Normaliza string para comparação (remove espaços extras, baixa caixa, normaliza acentos)
  const normalize = (s) => (s || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
  const nomeBate = normalize(nomeConfirmacao) === normalize(pacienteNome) && pacienteNome.length > 0;
  
  const handleClose = () => {
    if (dialogState.loading) return;
    setDialogState(initialCancelamentoState);
  };

  const handleConfirm = async () => {
    // Validações
    if (!dialogState.justificativa.trim()) {
      toast.error('Justificativa é obrigatória para cancelamento');
      return;
    }
    
    // Motivo do desligamento é obrigatório para todos os perfis no cancelamento definitivo
    if (dialogState.tipoCancelamento === 'definitivo' && 
        !dialogState.motivoDesligamento.trim()) {
      toast.error('Motivo do desligamento é obrigatório');
      return;
    }
    
    // Chamar callback do componente pai
    await onConfirm();
  };

  return (
    <AlertDialog 
      open={dialogState.open} 
      onOpenChange={(open) => {
        if (!dialogState.loading) {
          setDialogState(prev => ({ 
            ...prev, 
            open,
            step: 'escolha',
            tipoCancelamento: null,
            justificativa: '',
            motivoDesligamento: '',
            encaminhamento: ''
          }));
        }
      }}
    >
      <AlertDialogContent className="sm:max-w-[500px] max-h-[90vh] flex flex-col p-0 gap-0">
        <AlertDialogHeader className="p-6 pb-2 flex-shrink-0">
          <AlertDialogTitle style={{ fontFamily: 'Manrope, sans-serif', color: '#2C3632' }}>
            {dialogState.step === 'escolha' && 'Cancelar Agendamento'}
            {dialogState.step === 'simples' && 'Cancelamento Simples'}
            {dialogState.step === 'definitivo' && 'Desligamento Definitivo'}
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="sr-only">Diálogo de cancelamento de agendamento</div>
          </AlertDialogDescription>
        </AlertDialogHeader>

        {/* Corpo scrollável */}
        <div className="px-6 overflow-y-auto flex-1 min-h-0 space-y-3 pb-4">
            {/* Info do paciente sempre visível */}
            <div className="p-3 rounded-lg" style={{ backgroundColor: '#F8F6F3' }}>
              <p className="text-sm">
                Paciente: <strong style={{ color: '#2C3632' }}>
                  {dialogState.agendamento?.pacientes?.nome_completo}
                </strong>
              </p>
              <p className="text-sm">
                Data: <strong style={{ color: '#2C3632' }}>
                  {dialogState.agendamento?.data_hora_inicio && 
                    formatDateTime(dialogState.agendamento.data_hora_inicio, "dd/MM/yyyy 'às' HH:mm")}
                </strong>
              </p>
            </div>
              
              {/* Step 1: Escolha do tipo de cancelamento (apenas para profissionais/recepção) */}
              {dialogState.step === 'escolha' && (
                <div className="space-y-4 mt-4">
                  <p className="text-sm" style={{ color: '#5C6B64' }}>
                    Qual tipo de cancelamento deseja realizar?
                  </p>
                  
                  {/* Card - Cancelamento Simples */}
                  <div 
                    className="p-4 border-2 rounded-xl cursor-pointer transition-all hover:border-amber-400 hover:bg-amber-50"
                    style={{ borderColor: '#EAE6DF' }}
                    onClick={() => setDialogState(prev => ({ 
                      ...prev, 
                      step: 'simples', 
                      tipoCancelamento: 'simples' 
                    }))}
                    data-testid="btn-cancelamento-simples"
                  >
                    <div className="flex items-start gap-3">
                      <div className="p-2 rounded-lg" style={{ backgroundColor: '#FEF3C7' }}>
                        <XCircle className="h-5 w-5" style={{ color: '#D97706' }} />
                      </div>
                      <div>
                        <p className="font-semibold" style={{ color: '#2C3632' }}>Cancelamento Simples</p>
                        <p className="text-xs mt-1" style={{ color: '#5C6B64' }}>
                          Para faltas, reagendamentos ou cancelamentos pontuais. O paciente continua em acompanhamento.
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Card - Desligamento Definitivo */}
                  <div 
                    className="p-4 border-2 rounded-xl cursor-pointer transition-all hover:border-red-400 hover:bg-red-50"
                    style={{ borderColor: '#EAE6DF' }}
                    onClick={() => setDialogState(prev => ({ 
                      ...prev, 
                      step: 'definitivo', 
                      tipoCancelamento: 'definitivo' 
                    }))}
                    data-testid="btn-desligamento-definitivo"
                  >
                    <div className="flex items-start gap-3">
                      <div className="p-2 rounded-lg" style={{ backgroundColor: '#FEE2E2' }}>
                        <FileText className="h-5 w-5" style={{ color: '#DC2626' }} />
                      </div>
                      <div>
                        <p className="font-semibold" style={{ color: '#2C3632' }}>Desligamento Definitivo</p>
                        <p className="text-xs mt-1" style={{ color: '#5C6B64' }}>
                          Encerra o tratamento do paciente. Será gerado um <strong>Termo de Desligamento</strong> em PDF.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Step 2: Cancelamento Simples */}
              {dialogState.step === 'simples' && (
                <div className="mt-4">
                  <Label htmlFor="justificativa" className="text-sm font-medium">
                    Justificativa <span className="text-red-500">*</span>
                  </Label>
                  <Textarea
                    id="justificativa"
                    placeholder="Motivo do cancelamento..."
                    value={dialogState.justificativa}
                    onChange={(e) => setDialogState(prev => ({ ...prev, justificativa: e.target.value }))}
                    className="mt-2 min-h-[100px]"
                    maxLength={500}
                    data-testid="input-justificativa-simples"
                  />
                  <p className="text-xs text-gray-400 mt-1">
                    {dialogState.justificativa.length}/500 caracteres
                  </p>
                </div>
              )}
              
              {/* Step 3: Desligamento Definitivo */}
              {dialogState.step === 'definitivo' && (
                <div className="space-y-4 mt-4">
                  {/* Seletor de escopo - apenas para Recepção/Admin */}
                  {podeEscolherEscopo && dialogState.escopoProfissionalId && (
                    <div
                      className="p-3 rounded-lg space-y-2"
                      style={{ backgroundColor: '#F8F6F3', border: '1px solid #EAE6DF' }}
                      data-testid="escopo-desligamento"
                    >
                      <p className="text-xs font-semibold" style={{ color: '#2C3632' }}>
                        Abrangência do desligamento:
                      </p>
                      <label className="flex items-start gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="escopo-desligamento"
                          value="profissional"
                          checked={dialogState.escopoDesligamento === 'profissional'}
                          onChange={() => setDialogState(prev => ({ ...prev, escopoDesligamento: 'profissional' }))}
                          className="mt-1"
                          data-testid="radio-escopo-profissional"
                        />
                        <span className="text-xs" style={{ color: '#2C3632' }}>
                          <strong>Apenas com {dialogState.escopoProfissionalNome || 'este profissional'}</strong>
                          <br />
                          <span style={{ color: '#5C6B64' }}>
                            O paciente continua em atendimento com outros profissionais da clínica.
                          </span>
                        </span>
                      </label>
                      <label className="flex items-start gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="escopo-desligamento"
                          value="clinica"
                          checked={dialogState.escopoDesligamento === 'clinica'}
                          onChange={() => setDialogState(prev => ({ ...prev, escopoDesligamento: 'clinica' }))}
                          className="mt-1"
                          data-testid="radio-escopo-clinica"
                        />
                        <span className="text-xs" style={{ color: '#2C3632' }}>
                          <strong>Da clínica inteira</strong> (todos os profissionais)
                          <br />
                          <span style={{ color: '#5C6B64' }}>
                            Encerra o vínculo do paciente com a clínica.
                          </span>
                        </span>
                      </label>
                    </div>
                  )}

                  <div 
                    className="p-3 rounded-lg flex items-center gap-2"
                    style={{ backgroundColor: '#FEF2F2', border: '1px solid #FCA5A5' }}
                  >
                    <AlertCircle className="h-4 w-4" style={{ color: '#DC2626' }} />
                    <p className="text-xs" style={{ color: '#991B1B' }}>
                      <strong>Atenção:</strong> Esta ação gera um Termo de Desligamento oficial e cancela{' '}
                      {podeEscolherEscopo && dialogState.escopoDesligamento === 'profissional'
                        ? <>os agendamentos futuros deste paciente <strong>com {dialogState.escopoProfissionalNome || 'o profissional selecionado'}</strong>.</>
                        : <>todos os agendamentos futuros deste paciente.</>
                      }
                    </p>
                  </div>

                  {/* Preview do impacto */}
                  {loadingPreview && (
                    <div className="flex items-center gap-2 text-xs" style={{ color: '#5C6B64' }}>
                      <Loader2 className="h-3 w-3 animate-spin" />
                      Calculando impacto do desligamento...
                    </div>
                  )}
                  {!loadingPreview && preview && preview.total_agendamentos > 0 && (
                    <div
                      className="p-3 rounded-lg space-y-1.5"
                      style={{ backgroundColor: '#FFF7ED', border: '1px solid #FED7AA' }}
                      data-testid="preview-desligamento"
                    >
                      <div className="flex items-center gap-2">
                        <Users className="h-4 w-4" style={{ color: '#C2410C' }} />
                        <p className="text-sm font-semibold" style={{ color: '#9A3412' }}>
                          {preview.total_agendamentos} agendamento(s) será(ão) EXCLUÍDO(S)
                        </p>
                      </div>
                      {Array.isArray(preview.profissionais_afetados) && preview.profissionais_afetados.length > 0 && (
                        <ul className="text-xs pl-6 list-disc" style={{ color: '#9A3412' }}>
                          {preview.profissionais_afetados.map((p) => (
                            <li key={p.nome}>
                              <span className="font-medium">{p.nome.trim()}</span> — {p.quantidade} agendamento(s)
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  )}
                  
                  {/* Campos de Motivo e Encaminhamento - para todos os perfis */}
                  <div>
                    <Label className="text-sm font-medium">
                      Motivo do Desligamento <span className="text-red-500">*</span>
                    </Label>
                    <Textarea
                      placeholder="Descreva o motivo do desligamento..."
                      value={dialogState.motivoDesligamento}
                      onChange={(e) => setDialogState(prev => ({ ...prev, motivoDesligamento: e.target.value }))}
                      className="mt-2 min-h-[80px]"
                      maxLength={500}
                      data-testid="input-motivo-desligamento"
                    />
                  </div>
                  
                  <div>
                    <Label className="text-sm font-medium">
                      Encaminhamento / Orientações (opcional)
                    </Label>
                    <Textarea
                      placeholder="Se houver, informe encaminhamentos ou orientações finais..."
                      value={dialogState.encaminhamento}
                      onChange={(e) => setDialogState(prev => ({ ...prev, encaminhamento: e.target.value }))}
                      className="mt-2 min-h-[60px]"
                      maxLength={500}
                      data-testid="input-encaminhamento"
                    />
                  </div>
                  
                  <div>
                    <Label className="text-sm font-medium">
                      Justificativa do Cancelamento <span className="text-red-500">*</span>
                    </Label>
                    <Textarea
                      placeholder="Justificativa para registro interno..."
                      value={dialogState.justificativa}
                      onChange={(e) => setDialogState(prev => ({ ...prev, justificativa: e.target.value }))}
                      className="mt-2 min-h-[60px]"
                      maxLength={500}
                      data-testid="input-justificativa-definitivo"
                    />
                    <p className="text-xs text-gray-400 mt-1">
                      {dialogState.justificativa.length}/500 caracteres
                    </p>
                  </div>

                  {/* Confirmação final por digitação do nome do paciente */}
                  <div className="p-3 rounded-lg border-2" style={{ borderColor: '#DC2626', backgroundColor: '#FEF2F2' }}>
                    <Label className="text-sm font-semibold" style={{ color: '#991B1B' }}>
                      Confirmação final <span className="text-red-500">*</span>
                    </Label>
                    <p className="text-xs mt-1 mb-2" style={{ color: '#7F1D1D' }}>
                      Para confirmar, digite o nome completo do paciente exatamente como aparece abaixo:
                    </p>
                    <p className="text-xs font-mono p-2 rounded mb-2" style={{ backgroundColor: '#FFFFFF', color: '#2C3632', border: '1px solid #FCA5A5' }}>
                      {pacienteNome}
                    </p>
                    <Input
                      value={nomeConfirmacao}
                      onChange={(e) => setNomeConfirmacao(e.target.value)}
                      placeholder="Digite o nome do paciente..."
                      className="mt-1"
                      data-testid="input-confirmacao-nome-paciente"
                      autoComplete="off"
                    />
                    {nomeConfirmacao && !nomeBate && (
                      <p className="text-xs mt-1.5" style={{ color: '#DC2626' }}>
                        ✗ Nome não corresponde
                      </p>
                    )}
                    {nomeBate && (
                      <p className="text-xs mt-1.5" style={{ color: '#15803D' }}>
                        ✓ Nome confirmado
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
        {/* /Corpo scrollável */}
        
        <AlertDialogFooter className="flex-col sm:flex-row gap-2 p-6 pt-3 border-t flex-shrink-0" style={{ backgroundColor: '#FFFFFF' }}>
          {dialogState.step === 'escolha' && (
            <AlertDialogCancel 
              disabled={dialogState.loading}
              onClick={handleClose}
            >
              Voltar
            </AlertDialogCancel>
          )}
          
          {(dialogState.step === 'simples' || dialogState.step === 'definitivo') && (
            <>
              <Button 
                variant="outline"
                disabled={dialogState.loading}
                onClick={() => {
                  // Profissional e Recepção: voltar para escolha de tipo
                  // Admin: fechar o modal
                  if (user?.perfil === 'profissional' || user?.perfil === 'recepcao') {
                    setDialogState(prev => ({ 
                      ...prev, 
                      step: 'escolha',
                      tipoCancelamento: null,
                      justificativa: '',
                      motivoDesligamento: '',
                      encaminhamento: ''
                    }));
                  } else {
                    handleClose();
                  }
                }}
              >
                Voltar
              </Button>
              <Button
                disabled={
                  dialogState.loading || 
                  !dialogState.justificativa.trim() ||
                  (dialogState.step === 'definitivo' && !dialogState.motivoDesligamento.trim()) ||
                  (dialogState.step === 'definitivo' && !nomeBate)
                }
                onClick={handleConfirm}
                className={dialogState.step === 'definitivo' 
                  ? "bg-red-600 hover:bg-red-700 text-white" 
                  : "bg-amber-500 hover:bg-amber-600 text-white"}
                data-testid="btn-confirmar-cancelamento"
              >
                {dialogState.loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                {dialogState.step === 'definitivo' 
                  ? 'Confirmar Desligamento' 
                  : 'Confirmar Cancelamento'}
              </Button>
            </>
          )}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default CancelamentoDialog;
